import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FormField, form, required, submit } from '@angular/forms/signals';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { from } from 'rxjs';
import { parsePhoneNumber } from 'awesome-phonenumber';
import { HttpErrorResponse } from '@angular/common/http';
import { User, UsersApi } from '@tourfold/libraries/tf-api-client-public-v2';
import { SignalFormServerErrors } from '@tourfold/libraries/shared-util';
import { PhoneNumberComponent } from '@tourfold/libraries/shared-ui/phone-number/phone-number.component';
import { CurrentUserService } from '@tourfold/shared-kernels/current-user';
import {
  resolveSettingsLoadErrorDetails,
  SettingsActions,
  SettingsInlineMessage,
  SettingsLoadError,
  type SettingsLoadErrorDetails,
} from '@tourfold/features/settings-ui';

interface ProfileFormData {
  firstname: string;
  lastname: string;
  phoneNumber: string;
}

interface PasswordFormData {
  current: string;
  next: string;
  confirm: string;
}

const EMPTY_PROFILE: ProfileFormData = { firstname: '', lastname: '', phoneNumber: '' };
const EMPTY_PASSWORD: PasswordFormData = { current: '', next: '', confirm: '' };

@Component({
  selector: 'tf-settings-page-profile',
  imports: [
    CommonModule,
    FormsModule,
    FormField,
    TranslateModule,
    ButtonModule,
    InputTextModule,
    PasswordModule,
    PhoneNumberComponent,
    SettingsActions,
    SettingsInlineMessage,
    SettingsLoadError,
  ],
  templateUrl: './settings-user-profile.html',
  styleUrl: './settings-user-profile.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsUserProfile implements OnInit {
  private readonly usersApi = inject(UsersApi);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly currentUserService = inject(CurrentUserService);

  readonly isLoading = signal(false);
  readonly isSavingProfile = signal(false);
  readonly isSavingPassword = signal(false);
  readonly profileSubmitted = signal(false);
  readonly passwordSubmitted = signal(false);
  readonly hasLoadedOnce = signal(false);
  readonly initialLoadFailed = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly loadErrorDetails = signal<SettingsLoadErrorDetails | null>(null);
  readonly profileError = signal<string | null>(null);
  readonly passwordError = signal<string | null>(null);

  readonly currentUser = signal<User | null>(null);
  readonly currentUserEmail = computed(() => this.currentUser()?.email ?? '');

  readonly profileModel = signal<ProfileFormData>(EMPTY_PROFILE);
  readonly profileForm = form(this.profileModel, (schema) => {
    required(schema.firstname, {
      message: 'SETTINGS.USER_PROFILE.VALIDATION.FIRSTNAME_REQUIRED',
    });
    required(schema.lastname, {
      message: 'SETTINGS.USER_PROFILE.VALIDATION.LASTNAME_REQUIRED',
    });
  });

  // Phone number is optional. When the user does enter something, we run the
  // value through awesome-phonenumber (Google libphonenumber port) and surface
  // the invalid state inline; backend does not yet validate phones (TF-1830
  // follow-up filed against backend), so this is the only safeguard.
  readonly phoneNumberInvalid = computed(() => {
    const value = this.profileModel().phoneNumber;
    if (value === '' || value == null) return false;
    return !parsePhoneNumber(value).valid;
  });
  readonly phoneTouched = signal(false);
  readonly showPhoneClientError = computed(
    () => (this.profileSubmitted() || this.phoneTouched()) && this.phoneNumberInvalid()
  );
  readonly profileServerErrors = new SignalFormServerErrors(
    this.profileModel,
    ['firstname', 'lastname', 'phoneNumber'] as const,
    {
      pointerToField: {
        firstname: 'firstname',
        lastname: 'lastname',
        phone_number: 'phoneNumber',
        phoneNumber: 'phoneNumber',
      },
    }
  );

  readonly passwordModel = signal<PasswordFormData>(EMPTY_PASSWORD);
  readonly passwordForm = form(this.passwordModel, (schema) => {
    required(schema.current, {
      message: 'SETTINGS.USER_PROFILE.VALIDATION.CURRENT_PASSWORD_REQUIRED',
    });
    required(schema.next, {
      message: 'SETTINGS.USER_PROFILE.VALIDATION.NEW_PASSWORD_REQUIRED',
    });
    required(schema.confirm, {
      message: 'SETTINGS.USER_PROFILE.VALIDATION.CONFIRM_PASSWORD_REQUIRED',
    });
  });
  readonly passwordServerErrors = new SignalFormServerErrors(
    this.passwordModel,
    ['current', 'next', 'confirm'] as const,
    {
      pointerToField: {
        current_password: 'current',
        currentPassword: 'current',
        new_password: 'next',
        newPassword: 'next',
      },
    }
  );

  readonly hasMinLength = computed(() => /^.{8,}$/.test(this.passwordModel().next));
  readonly hasNumber = computed(() => /\d/.test(this.passwordModel().next));
  readonly hasUpperCase = computed(() => /[A-Z]/.test(this.passwordModel().next));
  readonly hasLowerCase = computed(() => /[a-z]/.test(this.passwordModel().next));
  readonly passwordsMatch = computed(() => {
    const next = this.passwordModel().next;
    return next !== '' && next === this.passwordModel().confirm;
  });
  readonly passwordPolicyOk = computed(
    () =>
      this.hasMinLength() &&
      this.hasNumber() &&
      this.hasUpperCase() &&
      this.hasLowerCase() &&
      this.passwordsMatch()
  );

  ngOnInit(): void {
    this.loadCurrentUser();
  }

  private loadCurrentUser(): void {
    const hadLoadedOnce = this.hasLoadedOnce();
    this.isLoading.set(true);
    from(this.usersApi.getCurrentUser())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (user) => {
          this.applyUser(user);
          this.profileServerErrors.clear();
          this.loadError.set(null);
          this.loadErrorDetails.set(null);
          this.initialLoadFailed.set(false);
          this.hasLoadedOnce.set(true);
          this.isLoading.set(false);
        },
        error: (error: unknown) => {
          void this.handleLoadError(error, hadLoadedOnce);
        },
      });
  }

  retryLoad(): void {
    this.loadCurrentUser();
  }

  private applyUser(user: User): void {
    this.currentUser.set(user);
    this.profileModel.set({
      firstname: user.firstname ?? '',
      lastname: user.lastname ?? '',
      phoneNumber: user.phoneNumber ?? '',
    });
  }

  cancelProfile(): void {
    this.profileServerErrors.clear();
    this.profileError.set(null);
    this.passwordError.set(null);
    this.profileSubmitted.set(false);
    this.phoneTouched.set(false);
    this.loadCurrentUser();
  }

  onPhoneNumberChange(value: string | null): void {
    const next = value ?? '';
    this.profileError.set(null);
    // Updating the model signal triggers SignalFormServerErrors's internal
    // effect, which auto-clears the matching field error on its own.
    this.profileModel.update((m) => ({ ...m, phoneNumber: next }));
  }

  onPhoneNumberBlur(): void {
    this.phoneTouched.set(true);
  }

  saveProfile(): void {
    this.profileSubmitted.set(true);
    this.phoneTouched.set(true);
    if (this.phoneNumberInvalid()) {
      // Client-side phone validation gate: the backend currently accepts any
      // phone string (TF-1830 follow-up), so we must stop the save here to
      // avoid persisting garbage. The inline red error already explains why.
      return;
    }
    submit(this.profileForm, async () => {
      const me = this.currentUser();
      if (!me) {
        return;
      }
      this.isSavingProfile.set(true);
      this.profileError.set(null);
      this.profileServerErrors.clear();
      const { firstname, lastname, phoneNumber } = this.profileModel();
      // Normalise to E.164 before sending — same shape the legacy drawer used,
      // and what the backend expects for downstream SMS/Twilio integrations.
      const normalisedPhone =
        phoneNumber !== ''
          ? (parsePhoneNumber(phoneNumber).number?.e164 ?? phoneNumber)
          : undefined;
      try {
        const updated = await this.usersApi.patchUser({
          userId: me.id,
          patchUser: {
            firstname,
            lastname,
            phoneNumber: normalisedPhone,
            lockVersion: me.lockVersion ?? 0,
          },
        });
        this.applyUser(updated);
        this.syncCurrentUserSession(updated);
        this.profileSubmitted.set(false);
        this.phoneTouched.set(false);
      } catch (error: unknown) {
        await this.handleProfileError(error);
      } finally {
        this.isSavingProfile.set(false);
      }
    });
  }

  // Mirror the updated firstname/lastname into the session-level CurrentUserService
  // so the header avatar / profile popover (driven by CurrentUserInfoService) reflect
  // the change immediately, without requiring a page refresh.
  private syncCurrentUserSession(updated: User): void {
    const current = this.currentUserService.user();
    if (!current) {
      return;
    }
    this.currentUserService.setUser({
      ...current,
      firstname: updated.firstname ?? current.firstname,
      lastname: updated.lastname ?? current.lastname,
    });
  }

  private async handleProfileError(error: unknown): Promise<void> {
    // 409 Conflict — another tab/session changed the profile under us.
    // Reload from server so the user retries against the fresh `lockVersion`.
    if (error instanceof HttpErrorResponse && error.status === 409) {
      this.profileError.set(this.translateService.instant('SETTINGS.USER_PROFILE.PROFILE.CONFLICT'));
      this.loadCurrentUser();
      return;
    }
    const applied = await this.profileServerErrors.apply(error);
    if (applied && applied.fieldCount > 0) {
      if (applied.generalMessages.length > 0) {
        this.profileError.set(applied.generalMessages.join(' '));
      }
      return;
    }
    const joinedGeneral = applied?.generalMessages.join(' ') ?? '';
    const fallback =
      joinedGeneral !== ''
        ? joinedGeneral
        : this.translateService.instant('SETTINGS.USER_PROFILE.PROFILE.SAVE_FAILED');
    this.profileError.set(fallback);
  }

  savePassword(): void {
    this.passwordSubmitted.set(true);
    submit(this.passwordForm, async () => {
      if (!this.passwordPolicyOk()) {
        return;
      }
      this.isSavingPassword.set(true);
      this.passwordError.set(null);
      this.passwordServerErrors.clear();
      const { current, next } = this.passwordModel();
      try {
        await this.usersApi.changeOwnPassword({
          changeOwnPassword: {
            currentPassword: current,
            newPassword: next,
          },
        });
        // reset() clears the values AND the per-field touched/dirty state, so
        // the now-empty required fields don't immediately repaint as invalid.
        this.passwordForm().reset(EMPTY_PASSWORD);
        this.passwordSubmitted.set(false);
      } catch (error: unknown) {
        await this.handlePasswordError(error);
      } finally {
        this.isSavingPassword.set(false);
      }
    });
  }

  private async handlePasswordError(error: unknown): Promise<void> {
    // 422 Unprocessable Entity — backend signals "wrong current password" or
    // "policy violation". We surface the right inline message based on what
    // the server returns. (401 isn't used because the global error interceptor
    // would refresh-and-retry on every 401 from non-/auth URLs.)
    if (error instanceof HttpErrorResponse && error.status === 422) {
      const applied = await this.passwordServerErrors.apply(error);
      if (applied && applied.fieldCount > 0) {
        return;
      }
      // No field-scoped message — fall back to the generic "current invalid"
      // hint, since that's by far the dominant 422 cause for this endpoint.
      this.passwordError.set(
        this.translateService.instant('SETTINGS.USER_PROFILE.PASSWORD.CURRENT_INVALID')
      );
      return;
    }
    if (error instanceof HttpErrorResponse && error.status === 400) {
      this.passwordError.set(
        this.translateService.instant('SETTINGS.USER_PROFILE.PASSWORD.POLICY_VIOLATION')
      );
      return;
    }
    const applied = await this.passwordServerErrors.apply(error);
    if (applied && applied.fieldCount > 0) {
      if (applied.generalMessages.length > 0) {
        this.passwordError.set(applied.generalMessages.join(' '));
      }
      return;
    }
    this.passwordError.set(
      this.translateService.instant('SETTINGS.USER_PROFILE.PASSWORD.CHANGE_FAILED')
    );
  }

  clearProfileError(): void {
    this.profileError.set(null);
  }

  clearPasswordError(): void {
    this.passwordError.set(null);
  }

  private async handleLoadError(error: unknown, hadLoadedOnce: boolean): Promise<void> {
    const message = this.translateService.instant('SETTINGS.USER_PROFILE.PROFILE.LOAD_FAILED');
    if (hadLoadedOnce) {
      this.profileError.set(message);
      this.loadError.set(null);
      this.loadErrorDetails.set(null);
      this.initialLoadFailed.set(false);
    } else {
      this.loadError.set(message);
      this.loadErrorDetails.set(await resolveSettingsLoadErrorDetails(error));
      this.initialLoadFailed.set(true);
    }
    this.isLoading.set(false);
  }
}
